/**
 * Arthaus Smart Art Facets (graph-native)
 * Phase: 9a-dynamic
 *
 * The collection left-nav, powered entirely by the Picasso concierge graph. On load and on
 * every selection it POSTs /api/collection-filter, which returns the discriminative facet
 * groups with ADAPTIVE drill-down counts + the matching page of artworks. Selecting facets
 * filters the grid in place (client-rendered tiles), updates the URL (shareable/back-button),
 * and recomputes the sidebar so dead options drop and counts update live.
 *
 * No Shopify product metafields / Search & Discovery — the graph is the filter engine.
 * Progressive enhancement: if JS/the API is unavailable, the native fallback stays.
 */
(function () {
  'use strict';

  var BUCKETS = ['palette', 'movement', 'mood', 'subject'];
  var URLKEY = { palette: 'fp', movement: 'fm', mood: 'fmo', subject: 'fs' };

  function esc(s) {
    return (s == null ? '' : String(s)).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function fmt(n) { return (n || 0).toLocaleString(); }

  // Fallback when discovery-grid.js isn't on the page — same shared card markup (assets/arthaus-card.js).
  function gridCard(p) {
    return window.ArthausCard ? window.ArthausCard.render(p) : '';
  }

  function Facets(root) {
    var self = this;
    this.root = root;
    this.api = (root.getAttribute('data-api') || '').replace(/\/$/, '');
    this.conceptKey = root.getAttribute('data-concept-key') || '';
    this.collectionHandle = root.getAttribute('data-collection-handle') || '';
    this.allHandles = null;                        // full collection pool (regular collections)
    this.perPage = parseInt(root.getAttribute('data-per-page'), 10) || 24;
    this.grid = document.querySelector('[data-product-grid]');
    this.originalGrid = this.grid ? this.grid.innerHTML : '';
    this.prerendered = root.getAttribute('data-prerendered') === 'true';
    this.sel = this.readUrl();
    this.page = 1;

    if (this.prerendered) {
      // Facets are already in the HTML (precomputed metafield) — just wire them up.
      this.hydrate();
      if (this.hasSelection()) this.ensureHandles().then(function () { self.load(false); }); // re-apply URL filter
    } else {
      var cached = this.readCache();               // Layer 4: instant from sessionStorage
      if (cached) this.renderSidebar(cached);
      // Regular collections have no concept_key → reason over the collection's own products.
      this.ensureHandles().then(function () { self.load(true); });
    }
  }

  // Resolve the full pool once. Concept pages use concept_key; regular collections fetch
  // their whole product-handle list (shared, cached) so facets describe the actual set.
  Facets.prototype.ensureHandles = function () {
    var self = this;
    if (this.conceptKey || this.allHandles) return Promise.resolve(this.allHandles);
    if (window.ArthausGrid && window.ArthausGrid.collectionHandles && this.collectionHandle) {
      return window.ArthausGrid.collectionHandles(this.collectionHandle)
        .then(function (list) { self.allHandles = list && list.length ? list : null; return self.allHandles; });
    }
    return Promise.resolve(null);
  };

  // Bind the server-rendered facets + normalise their counts (add thousands separators).
  Facets.prototype.hydrate = function () {
    var host = this.root.querySelector('[data-gf-host]');
    if (!host) { this.prerendered = false; return; }
    host.querySelectorAll('[data-gf-count]').forEach(function (el) {
      el.textContent = fmt(parseInt((el.textContent || '').replace(/[^0-9]/g, ''), 10) || 0);
    });
    var tot = host.querySelector('[data-gf-total]');
    if (tot) tot.textContent = fmt(parseInt(tot.getAttribute('data-gf-total'), 10) || 0) + ' works';
    this.bind(host);
  };

  Facets.prototype.cacheKey = function () {
    return 'gf:' + (this.conceptKey || this.root.getAttribute('data-collection-handle') || '');
  };
  Facets.prototype.readCache = function () {
    try { var s = sessionStorage.getItem(this.cacheKey()); return s ? JSON.parse(s) : null; } catch (e) { return null; }
  };
  Facets.prototype.writeCache = function (d) {
    try { sessionStorage.setItem(this.cacheKey(), JSON.stringify(d)); } catch (e) { /* quota */ }
  };

  Facets.prototype.pool = function () {
    if (this.conceptKey) return { concept_key: this.conceptKey };
    if (this.allHandles && this.allHandles.length) return { handles: this.allHandles };
    // Fallback (products.json unavailable): the visible page's handles.
    var h = Array.prototype.slice.call(document.querySelectorAll('[data-product-grid] a[href*="/products/"]'))
      .map(function (a) { return (a.getAttribute('href').split('/products/')[1] || '').split('?')[0]; }).filter(Boolean);
    return { handles: h.filter(function (x, i) { return h.indexOf(x) === i; }) };
  };

  Facets.prototype.readUrl = function () {
    var q = new URLSearchParams(location.search), sel = {};
    BUCKETS.forEach(function (b) { var v = q.get(URLKEY[b]); sel[b] = v ? v.split('~').filter(Boolean) : []; });
    return sel;
  };
  Facets.prototype.writeUrl = function () {
    var q = new URLSearchParams(location.search);
    BUCKETS.forEach(function (b) { q.delete(URLKEY[b]); if (this.sel[b] && this.sel[b].length) q.set(URLKEY[b], this.sel[b].join('~')); }, this);
    var s = q.toString();
    history.replaceState(null, '', location.pathname + (s ? '?' + s : '') + location.hash);
  };
  Facets.prototype.hasSelection = function () {
    return BUCKETS.some(function (b) { return this.sel[b] && this.sel[b].length; }, this);
  };

  Facets.prototype.load = function (initial) {
    var self = this;
    var body = this.pool();
    body.selected = this.sel;
    body.page = this.page;
    body.per_page = this.perPage;
    // Show the intelligent loading state only when we're about to replace the grid.
    if (this.hasSelection() && this.grid && window.ArthausGrid) window.ArthausGrid.beginLoad(this.grid, this.perPage);

    fetch(this.api + '/api/collection-filter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Arthaus-Client': 'storefront' },
      body: JSON.stringify(body)
    })
      .then(function (r) { if (!r.ok) throw new Error('filter ' + r.status); return r.json(); })
      .then(function (d) {
        self.renderSidebar(d);
        if (!self.hasSelection()) self.writeCache(d);   // Layer 4: prime sessionStorage
        // Only take over the grid once the shopper actually filters (avoid a load-time flash).
        if (self.hasSelection()) self.renderGrid(d, false);
        else if (!initial) self.restoreGrid();
      })
      .catch(function () { if (window.ArthausGrid) window.ArthausGrid.endLoad(self.grid); /* keep server grid */ });
  };

  Facets.prototype.renderSidebar = function (d) {
    var self = this;
    var html = '';
    // Summary + clear
    html += '<div class="flex items-baseline justify-between mb-5" data-gf-summary>' +
      '<span class="font-mono text-xs text-mute uppercase tracking-wider">' + fmt(d.total) + ' works</span>' +
      (this.hasSelection() ? '<button type="button" data-gf-clear class="font-body text-xs text-bronze hover:underline">Clear all</button>' : '') +
    '</div>';
    // Active chips
    if (this.hasSelection()) {
      html += '<div class="flex flex-wrap gap-1.5 mb-5" data-gf-active>';
      BUCKETS.forEach(function (b) {
        (self.sel[b] || []).forEach(function (v) {
          html += '<button type="button" data-gf-remove data-b="' + b + '" data-v="' + esc(v) + '" class="inline-flex items-center gap-1 rounded-full bg-charcoal text-white px-2.5 py-1 font-body text-xs">' + esc(v) + ' <span class="opacity-70">✕</span></button>';
        });
      });
      html += '</div>';
    }
    // Groups
    (d.groups || []).forEach(function (g) {
      html += '<div class="border-b border-border/50 pb-5 mb-5"><p class="font-body text-sm font-medium text-charcoal mb-3">' + esc(g.group) + '</p><div class="space-y-1.5">';
      g.facets.forEach(function (f) {
        html += '<label class="flex items-center gap-2.5 cursor-pointer group">' +
          '<input type="checkbox" data-gf-facet data-b="' + g.bucket + '" data-v="' + esc(f.value) + '" ' + (f.selected ? 'checked' : '') + ' class="w-4 h-4 rounded-sm border-border text-bronze focus:ring-bronze cursor-pointer">' +
          '<span class="font-body text-sm text-charcoal group-hover:text-bronze transition-colors font-light">' + esc(f.label) + '</span>' +
          '<span class="font-mono text-xs text-mute ml-auto">' + fmt(f.count) + '</span>' +
        '</label>';
      });
      html += '</div></div>';
    });

    var fb = this.root.querySelector('[data-gf-fallback]');
    if (fb) fb.remove();
    var host = this.root.querySelector('[data-gf-host]');
    if (!host) { host = document.createElement('div'); host.setAttribute('data-gf-host', ''); this.root.appendChild(host); }
    host.innerHTML = html;
    this.bind(host);
  };

  Facets.prototype.bind = function (host) {
    var self = this;
    host.querySelectorAll('[data-gf-facet]').forEach(function (cb) {
      cb.addEventListener('change', function () { self.toggle(cb.getAttribute('data-b'), cb.getAttribute('data-v'), cb.checked); });
    });
    host.querySelectorAll('[data-gf-remove]').forEach(function (b) {
      b.addEventListener('click', function () { self.toggle(b.getAttribute('data-b'), b.getAttribute('data-v'), false); });
    });
    var clr = host.querySelector('[data-gf-clear]');
    if (clr) clr.addEventListener('click', function () { self.clear(); });
  };

  Facets.prototype.toggle = function (bucket, value, on) {
    var arr = this.sel[bucket] || (this.sel[bucket] = []);
    var i = arr.indexOf(value);
    if (on && i === -1) arr.push(value);
    if (!on && i !== -1) arr.splice(i, 1);
    this.page = 1;
    this.writeUrl();
    this.load(false);
  };
  Facets.prototype.clear = function () {
    BUCKETS.forEach(function (b) { this.sel[b] = []; }, this);
    this.page = 1;
    this.writeUrl();
    this.restoreGrid();
    this.load(false);
  };

  Facets.prototype.renderGrid = function (d, append) {
    if (!this.grid) return;
    if (window.ArthausGrid) {
      window.ArthausGrid.render(this.grid, d.products, { mlt: true, append: append });
    } else {
      var html = (d.products || []).map(gridCard).filter(Boolean).join('');
      if (append) this.grid.insertAdjacentHTML('beforeend', html);
      else this.grid.innerHTML = html || '<p class="col-span-full font-body text-sm text-mute py-10">No works match these filters.</p>';
    }
    this.renderLoadMore(d);
  };
  Facets.prototype.restoreGrid = function () {
    if (this.grid) this.grid.innerHTML = this.originalGrid;
    var lm = document.querySelector('[data-gf-more]'); if (lm) lm.remove();
  };
  Facets.prototype.renderLoadMore = function (d) {
    var self = this;
    var ex = document.querySelector('[data-gf-more]'); if (ex) ex.remove();
    if (!d.has_more || !this.grid) return;
    var btn = document.createElement('div');
    btn.setAttribute('data-gf-more', '');
    btn.className = 'mt-10 text-center';
    btn.innerHTML = '<button type="button" class="font-body text-sm border border-border rounded-full px-6 py-2.5 hover:bg-charcoal hover:text-white transition-colors">Load more</button>';
    this.grid.parentNode.appendChild(btn);
    btn.querySelector('button').addEventListener('click', function () {
      self.page += 1;
      var body = self.pool(); body.selected = self.sel; body.page = self.page; body.per_page = self.perPage;
      fetch(self.api + '/api/collection-filter', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Arthaus-Client': 'storefront' }, body: JSON.stringify(body) })
        .then(function (r) { return r.json(); }).then(function (dd) { self.renderGrid(dd, true); });
    });
  };

  function init() {
    var root = document.querySelector('[data-graph-facets]');
    if (!root || root._facets) return;
    root._facets = new Facets(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
